/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './Radio.scss';

import classnames from 'classnames';
import noop from 'lodash/noop';
import { bool, func, string } from 'prop-types';
import PropTypes from 'prop-types';
import React from 'react';

const handleChange = (onChange, value, props) => {
  if (props && !props.disabled) {
    return onChange(value);
  }
  if (props && props.onChange && !props.disabled) {
    return props.onChange(value);
  }
  return noop();
};
/**
 * This component restyles the radio button, which is trickier than it sounds.
 * The native radio element cannot be styled with CSS.
 * So it has to be done using other elements, and covering up the actual radio element.
 * The radio that you see is made with the ::before pseudo-element.
 * But it cannot be done with the radio's ::before because technically, it shouldn't have one.
 * For more details on that check out https://stackoverflow.com/a/4660434,
 * but in summary, input elements aren't supposed to have child elements, and psudo-elements count as children.
 * It would work in Chrome and Firefox, but IE actually observes the rule.
 * This is why the styles are applied to a sibling label element,
 * which is also why most of the css rules are `&{some input state} + .restyled-radio::before`
 */
const Radio = ({ className, dataTestId, onChange, value, ...props }) => (
  <div
    className="radio-container"
    onClick={() => handleChange(onChange, value, props)}>
    {/* the prop passed to Radio is called on change to mimic the native radio api */}
    <input
      className={classnames('radio', className)}
      data-testid={dataTestId}
      type="radio"
      {...props}
      onChange={noop} // this should be okay because of the explanation above that handle change is hijacked on the div wrapper
    />
    <label className="restyled-radio" htmlFor={props.name} />
  </div>
);

Radio.propTypes = {
  checked: bool.isRequired,
  className: string.isRequired,
  dataTestId: string,
  disabled: bool.isRequired,
  name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: func.isRequired
};

Radio.defaultProps = {
  className: '',
  disabled: false,
  onChange: noop
};

export default Radio;
